import { Injectable } from '@angular/core';
import { PriorityCode } from '../../domain/enums/priority-code.enum';
import { CreateEmployeeInvestigationCommand } from '../../domain/commands';
import { InvestigationEntity } from '../../domain/entities/investigation.entity';
import { InvestigationStatusSnapshotEntity } from '../../domain/entities/investigation-status-snapshot.entity';
import { InvestigationPrioritySnapshotEntity } from '../../domain/entities/investigation-priority-snapshot.entity';
import { InvestigationFieldValueEntity } from '../../domain/entities/investigation-field-value.entity';
import { InvestigationAttachmentEntity } from '../../domain/entities/investigation-attachment.entity';
import { InvestigationInvolvedEmployeeEntity } from '../../domain/entities/investigation-involved-employee.entity';
import { InvestigationWatcherEntity } from '../../domain/entities/investigation-watcher.entity';
import { WatcherOptionEntity } from '../../domain/entities/watcher-option.entity';
import { CreateInvestigationRequestDto } from '../dtos/create-investigation-request.dto';
import { CreateEmployeeInvestigationRequestDto } from '../dtos/create-employee-investigation-request.dto';
import { CreateInvestigationAttachmentDto } from '../dtos/create-investigation-attachment.dto';
import { InvestigationResponseDto } from '../dtos/investigation-response.dto';
import { InvestigationStatusSnapshotResponseDto } from '../dtos/investigation-status-snapshot-response.dto';
import { InvestigationPrioritySnapshotResponseDto } from '../dtos/investigation-priority-snapshot-response.dto';
import { InvestigationFieldValueResponseDto } from '../dtos/investigation-field-value-response.dto';
import { InvestigationAttachmentResponseDto } from '../dtos/investigation-attachment-response.dto';
import { InvestigationInvolvedEmployeeResponseDto } from '../dtos/investigation-involved-employee-response.dto';
import { InvestigationWatcherResponseDto } from '../dtos/investigation-watcher-response.dto';
import { InvestigationWatcherOptionResponseDto } from '../dtos/investigation-watcher-option-response.dto';
import { InvestigationMessageResponseDto } from '../dtos/investigation-message-response.dto';
import { InvestigationWorkNoteResponseDto } from '../dtos/investigation-work-note-response.dto';
import { InvestigationMessageEntity } from '../../domain/entities/investigation-message.entity';
import { InvestigationWorkNoteEntity } from '../../domain/entities/investigation-work-note.entity';
import { InvestigationLinkedInvestigationResponseDto } from '../dtos/investigation-linked-investigation-response.dto';
import { InvestigationLinkedInvestigationEntity } from '../../domain/entities/investigation-linked-investigation.entity';
import { InvestigationClosingReasonResponseDto } from '../dtos/investigation-closing-reason-response.dto';
import { InvestigationClosingReasonEntity } from '../../domain/entities/investigation-closing-reason.entity';
import { InvestigationConversionReasonResponseDto } from '../dtos/investigation-conversion-reason-response.dto';
import { InvestigationConversionReasonEntity } from '../../domain/entities/investigation-conversion-reason.entity';
import { InvestigationReopeningReasonResponseDto } from '../dtos/investigation-reopening-reason-response.dto';
import { InvestigationReopeningReasonEntity } from '../../domain/entities/investigation-reopening-reason.entity';
import { InvestigationChecklistStepResponseDto } from '../dtos/investigation-checklist-step-response.dto';
import { InvestigationChecklistStepEntity } from '../../domain/entities/investigation-checklist-step.entity';
import { InvestigationEmailTemplateSnapshotResponseDto } from '../dtos/investigation-email-template-snapshot-response.dto';
import { InvestigationEmailTemplateSnapshotEntity } from '../../domain/entities/investigation-email-template-snapshot.entity';
import { TransferredCaseFieldValueResponseDto } from '../dtos/transferred-case-field-value-response.dto';
import { TransferredCaseFieldValueEntity } from '../../domain/entities/transferred-case-field-value.entity';

@Injectable({ providedIn: 'root' })
export class InvestigationsMapper {
  public toCreateDto(data: Partial<InvestigationEntity>): CreateInvestigationRequestDto {
    return {
      investigationTypeId: data.investigationTypeId!,
      investigationFormVersionId: data.investigationFormVersionId!,
      requestedByUserId: data.requestorUserId!,
      priorityCode: data.priorityCode ?? PriorityCode.Medium,
      primaryAssigneeUserId: data.primaryAssigneeUserId ?? null,
      secondaryAssigneeUserId: data.secondaryAssigneeUserId ?? null,
      involvedEmployeeIds: data.involvedEmployeeIds ?? [],
      fieldValues: (data.fieldValues ?? []).map((fv) => ({
        versionFieldId: fv.versionFieldId,
        value: fv.value,
      })),
      fileUploads: (data.fileUploads ?? []).map((fu) => ({
        versionFieldId: fu.versionFieldId,
        fileName: fu.fileName,
        filePath: fu.filePath,
        fileSizeBytes: fu.fileSizeBytes,
        contentType: fu.contentType,
      })),
      attachments: this._mapAttachments(data.attachments ?? []),
    };
  }

  /**
   * Maps Create Employee Investigation command to employee investigation request DTO.
   * Used for employee portal investigation creation endpoint.
   * Note: Does NOT include requestorUserId or primaryAssigneeUserId - backend sets these from auth context.
   * @param command - Create employee investigation command
   * @returns Employee create investigation request DTO
   */
  public toCreateEmployeeDto(command: CreateEmployeeInvestigationCommand): CreateEmployeeInvestigationRequestDto {
    return {
      investigationTypeId: command.investigationTypeId,
      investigationFormVersionId: command.investigationFormVersionId,
      priorityCode: command.priorityCode,
      secondaryAssigneeUserId: command.secondaryAssigneeUserId ?? null,
      involvedEmployeeIds: command.involvedEmployeeIds,
      fieldValues: command.fieldValues.map((fv) => ({
        versionFieldId: fv.versionFieldId,
        value: fv.value,
      })),
      fileUploads: command.fileUploads.map((fu) => ({
        versionFieldId: fu.versionFieldId,
        fileName: fu.fileName,
        filePath: fu.filePath,
        fileSizeBytes: fu.fileSizeBytes,
        contentType: fu.contentType,
      })),
      attachments: this._mapAttachments(command.attachments),
      requestedByUserId: command.requestedByUserId ?? null,
    };
  }

  private _mapAttachments(attachments: InvestigationAttachmentEntity[]): CreateInvestigationAttachmentDto[] {
    return attachments.map((a) => ({
      id: a.id,
      fileName: a.fileName,
      filePath: a.filePath,
      fileSizeBytes: a.fileSizeBytes,
      contentType: a.contentType,
    }));
  }

  public toDomain(dto: InvestigationResponseDto): InvestigationEntity {
    const entity = new InvestigationEntity();
    entity.id = dto.id;
    entity.investigationNumber = dto.investigationNumber;
    entity.investigationTypeId = dto.investigationTypeId;
    entity.investigationTypeName = dto.investigationTypeName;
    entity.investigationTypeCategoryName = dto.investigationTypeCategoryName;
    entity.investigationFormVersionId = dto.investigationFormVersionId;
    entity.requestorUserId = dto.requestedByUserId;
    entity.requestedByName = dto.requestedByName;
    entity.requestedByEmail = dto.requestedByEmail;
    entity.requestedByPhone = dto.requestedByPhone ?? null;
    entity.requestedByDivision = dto.requestedByDivision ?? null;
    entity.requestedByDepartment = dto.requestedByDepartment ?? null;
    entity.primaryAssigneeUserId = dto.primaryAssigneeUserId ?? null;
    entity.primaryAssigneeName = dto.primaryAssigneeName ?? null;
    entity.secondaryAssigneeUserId = dto.secondaryAssigneeUserId ?? null;
    entity.secondaryAssigneeName = dto.secondaryAssigneeName ?? null;
    entity.statusId = dto.statusId;
    entity.statusName = dto.statusName;
    entity.statusDisplayName = dto.statusDisplayName;
    entity.statusColor = dto.statusColor;
    entity.statusCategoryId = dto.statusCategoryId;
    entity.statusCategoryName = dto.statusCategoryName;
    entity.priorityCode = dto.priorityCode as PriorityCode;
    entity.priorityName = dto.priorityName;
    entity.timeToResolveHours = dto.timeToResolveHours ?? null;
    entity.atRiskThresholdPercent = dto.atRiskThresholdPercent ?? null;
    entity.dueDate = dto.dueDate ?? null;
    entity.createdOn = dto.createdOn;
    entity.updatedOn = dto.updatedOn;
    entity.createdByUserId = dto.createdByUserId;
    entity.createdByUserName = dto.createdByUserName;
    entity.watcherCount = dto.watcherCount;
    entity.isCurrentUserWatching = dto.isCurrentUserWatching;
    entity.closingReasonId = dto.closingReasonId ?? null;
    entity.closingReasonName = dto.closingReasonName ?? null;
    entity.closingNotes = dto.closingNotes ?? null;
    entity.closedOn = dto.closedOn ?? null;
    entity.closedByUserId = dto.closedByUserId ?? null;
    entity.closedByUserName = dto.closedByUserName ?? null;
    entity.isInvestigation = dto.isInvestigation ?? false;
    entity.conversionReasonId = dto.conversionReasonId ?? null;
    entity.conversionReasonName = dto.conversionReasonName ?? null;
    entity.conversionNotes = dto.conversionNotes ?? null;
    entity.convertedOn = dto.convertedOn ?? null;
    entity.convertedByUserId = dto.convertedByUserId ?? null;
    entity.convertedByUserName = dto.convertedByUserName ?? null;
    entity.sourceCaseNumber = dto.sourceCaseNumber ?? null;
    entity.isPendingReclassification = dto.isPendingReclassification ?? false;
    entity.workflowSubStatus = dto.workflowSubStatus ?? null;
    entity.isReadOnly = dto.isReadOnly ?? false;
    entity.currentWorkflowHandoffTeamName = dto.currentWorkflowHandoffTeamName ?? null;
    entity.currentWorkflowHandoffTargetUserName = dto.currentWorkflowHandoffTargetUserName ?? null;
    entity.involvedEmployeeIds = [];
    entity.fieldValues = [];
    entity.fileUploads = [];
    return entity;
  }

  public toStatusSnapshotsDomain(dtos: InvestigationStatusSnapshotResponseDto[]): InvestigationStatusSnapshotEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationStatusSnapshotEntity();
      entity.id = dto.id;
      entity.sourceStatusId = dto.sourceStatusId;
      entity.name = dto.name;
      entity.displayName = dto.displayName;
      entity.color = dto.color;
      entity.categoryId = dto.categoryId;
      entity.categoryName = dto.categoryName;
      entity.isActive = dto.isActive;
      entity.sortOrder = dto.sortOrder;
      return entity;
    });
  }

  public toPrioritySnapshotsDomain(dtos: InvestigationPrioritySnapshotResponseDto[]): InvestigationPrioritySnapshotEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationPrioritySnapshotEntity();
      entity.id = dto.id;
      entity.priority = dto.priority;
      entity.name = dto.name;
      // Use full snapshot name as code — backend GetPrioritySnapshotByName matches on Name field
      entity.code = dto.name;
      entity.sortOrder = dto.sortOrder;
      entity.timeToResolveHours = dto.timeToResolveHours;
      entity.atRiskThresholdPercent = dto.atRiskThresholdPercent;
      entity.isActive = dto.isActive;
      return entity;
    });
  }

  public toFieldValuesDomain(dtos: InvestigationFieldValueResponseDto[]): InvestigationFieldValueEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationFieldValueEntity();
      entity.id = dto.id;
      entity.versionFieldId = dto.versionFieldId;
      entity.fieldLabel = dto.fieldLabel;
      entity.fieldType = dto.fieldType;
      entity.value = dto.value;
      entity.isRequired = dto.isRequired;
      entity.isVisibleToRequester = dto.isVisibleToRequester;
      entity.sortOrder = dto.sortOrder;
      entity.optionsJson = dto.optionsJson;
      return entity;
    });
  }

  public toAttachmentsDomain(dtos: InvestigationAttachmentResponseDto[]): InvestigationAttachmentEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationAttachmentEntity();
      entity.id = dto.id;
      entity.fileName = dto.fileName;
      entity.filePath = dto.filePath;
      entity.fileSizeBytes = dto.fileSizeBytes;
      entity.contentType = dto.contentType;
      entity.uploadedByUserId = dto.uploadedByUserId;
      entity.uploadedByUserName = dto.uploadedByUserName;
      entity.uploadedOn = dto.createdOn ? new Date(dto.createdOn) : undefined;
      return entity;
    });
  }

  public toInvolvedEmployeesDomain(dtos: InvestigationInvolvedEmployeeResponseDto[]): InvestigationInvolvedEmployeeEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationInvolvedEmployeeEntity();
      entity.userId = dto.userId;
      entity.firstName = dto.firstName;
      entity.lastName = dto.lastName;
      entity.email = dto.email;
      entity.phone = dto.phone ?? null;
      return entity;
    });
  }

  public toWatchersDomain(dtos: InvestigationWatcherResponseDto[]): InvestigationWatcherEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationWatcherEntity();
      entity.watchId = dto.watchId;
      entity.watcherUserId = dto.watcherUserId;
      entity.watcherName = dto.watcherName;
      entity.watchedOn = dto.watchedOn;
      return entity;
    });
  }

  public toWatcherOptionsDomain(dtos: InvestigationWatcherOptionResponseDto[]): WatcherOptionEntity[] {
    return dtos.map((dto) => {
      const entity = new WatcherOptionEntity();
      entity.userId = dto.userId;
      entity.firstName = dto.firstName;
      entity.lastName = dto.lastName;
      entity.email = dto.email;
      entity.isManager = dto.isManager;
      return entity;
    });
  }

  public toMessagesDomain(dtos: InvestigationMessageResponseDto[]): InvestigationMessageEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationMessageEntity();
      entity.id = dto.id;
      entity.messageType = dto.messageType;
      entity.body = dto.body;
      entity.senderUserId = dto.senderUserId;
      entity.senderName = dto.senderName;
      entity.senderFirstName = dto.senderFirstName;
      entity.senderLastName = dto.senderLastName;
      entity.isRequestor = dto.isRequestor;
      entity.createdOn = dto.createdOn;
      return entity;
    });
  }

  public toWorkNotesDomain(dtos: InvestigationWorkNoteResponseDto[]): InvestigationWorkNoteEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationWorkNoteEntity();
      entity.id = dto.id;
      entity.body = dto.body;
      entity.senderUserId = dto.senderUserId;
      entity.senderName = dto.senderName;
      entity.senderFirstName = dto.senderFirstName;
      entity.senderLastName = dto.senderLastName;
      entity.noteType = dto.noteType;
      entity.createdOn = dto.createdOn;
      return entity;
    });
  }

  public toLinkedInvestigationsDomain(dtos: InvestigationLinkedInvestigationResponseDto[]): InvestigationLinkedInvestigationEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationLinkedInvestigationEntity();
      entity.id = dto.id;
      entity.linkedInvestigationId = dto.linkedInvestigationId;
      entity.linkedInvestigationNumber = dto.linkedInvestigationNumber;
      entity.linkedInvestigationTypeName = dto.linkedInvestigationTypeName;
      entity.linkedInvestigationTypeCategoryName = dto.linkedInvestigationTypeCategoryName;
      entity.linkedInvestigationStatusName = dto.linkedInvestigationStatusName;
      entity.linkedInvestigationStatusColor = dto.linkedInvestigationStatusColor;
      entity.linkedInvestigationRequestorName = dto.linkedInvestigationRequestorName;
      entity.linkedInvestigationCreatedOn = dto.linkedInvestigationCreatedOn;
      entity.linkType = dto.linkType;
      entity.isAutoLinked = dto.isAutoLinked ?? false;
      return entity;
    });
  }

  public toChecklistStepsDomain(dtos: InvestigationChecklistStepResponseDto[]): InvestigationChecklistStepEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationChecklistStepEntity();
      entity.id = dto.id;
      entity.description = dto.description;
      entity.displayOrder = dto.displayOrder;
      entity.isCompleted = dto.isCompleted;
      return entity;
    });
  }

  public toEmailTemplateSnapshotsDomain(dtos: InvestigationEmailTemplateSnapshotResponseDto[]): InvestigationEmailTemplateSnapshotEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationEmailTemplateSnapshotEntity();
      entity.id = dto.id;
      entity.name = dto.name;
      entity.subject = dto.subject;
      entity.body = dto.body;
      entity.isActive = dto.isActive;
      return entity;
    });
  }

  public toClosingReasonsDomain(dtos: InvestigationClosingReasonResponseDto[]): InvestigationClosingReasonEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationClosingReasonEntity();
      entity.id = dto.id;
      entity.name = dto.name;
      entity.sortOrder = dto.sortOrder;
      return entity;
    });
  }

  public toConversionReasonsDomain(dtos: InvestigationConversionReasonResponseDto[]): InvestigationConversionReasonEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationConversionReasonEntity();
      entity.id = dto.id;
      entity.name = dto.name;
      entity.sortOrder = dto.sortOrder;
      return entity;
    });
  }

  public toReopeningReasonsDomain(dtos: InvestigationReopeningReasonResponseDto[]): InvestigationReopeningReasonEntity[] {
    return dtos.map((dto) => {
      const entity = new InvestigationReopeningReasonEntity();
      entity.id = dto.id;
      entity.name = dto.name;
      entity.sortOrder = dto.sortOrder;
      return entity;
    });
  }

  public toTransferredCaseFieldValuesDomain(dtos: TransferredCaseFieldValueResponseDto[]): TransferredCaseFieldValueEntity[] {
    return dtos.map((dto) => {
      const entity = new TransferredCaseFieldValueEntity();
      entity.fieldLabel = dto.fieldLabel;
      entity.fieldType = dto.fieldType;
      entity.valueJson = dto.valueJson;
      entity.sortOrder = dto.sortOrder;
      return entity;
    });
  }
}
